import React, { useState, useRef, useEffect } from 'react';
import { fetchOpenAIText } from '../../utils/openaiClient';
import './CreateDialogueExercise.css';

const CreateDialogueExercise = ({ dialogue, title, level, topic, onClose, updateAttributes, node: _node, editor: _editor, getPos: _getPos, isStudent = false, myState = {}, otherState = {}, updateState = () => {} }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editableTitle, setEditableTitle] = useState(title);
  const [editableLevel, setEditableLevel] = useState(level);
  const [editableTopic, setEditableTopic] = useState(topic);
  const [showInstructions, setShowInstructions] = useState(false);
  const [editableDialogue, setEditableDialogue] = useState(dialogue);
  const [genLoading, setGenLoading] = useState(false);
  const [genError, setGenError] = useState('');
  const [discussionQuestions, setDiscussionQuestions] = useState([]);
  const [questionsShared, setQuestionsShared] = useState(false);

  // Restore on mount: if teacher previously shared questions, show them
  useEffect(() => {
    if (isStudent) {
      if (Array.isArray(otherState.questions) && otherState.questions.length > 0 && otherState.questionsVisible) {
        setDiscussionQuestions(otherState.questions);
        setQuestionsShared(true);
      }
    } else {
      if (Array.isArray(myState.questions) && myState.questions.length > 0) {
        setDiscussionQuestions(myState.questions);
        setQuestionsShared(!!myState.questionsVisible);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time: student receives questions when teacher shares
  useEffect(() => {
    if (!isStudent) return;
    if (!otherState.questionsVisible) {
      setQuestionsShared(false);
      return;
    }
    if (Array.isArray(otherState.questions) && otherState.questions.length > 0) {
      setDiscussionQuestions(otherState.questions);
      setQuestionsShared(true);
    }
  }, [otherState.questionsVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDialogue = (dialogueData) => {
    if (typeof dialogueData === 'string') return dialogueData;
    if (typeof dialogueData === 'object' && dialogueData !== null) {
      return Object.entries(dialogueData).map(([speaker, text]) => `${speaker}: ${text}`).join('\n');
    }
    return '';
  };

  const getShortTitle = (fullText) => {
    if (!fullText) return '';
    const firstSentence = fullText.split(/[.!?]/)[0];
    const shortTitle = firstSentence.length > 50 ? firstSentence.substring(0, 50) + '...' : firstSentence;
    return shortTitle.trim();
  };

  const handleSave = () => {
    setIsEditing(false);
    if (updateAttributes) {
      updateAttributes({
        dialogue: editableDialogue,
        title: editableTitle,
        level: editableLevel,
        topic: editableTopic,
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditableTitle(title);
    setEditableLevel(level);
    setEditableTopic(topic);
    setEditableDialogue(formatDialogue(dialogue));
  };

  const handleBold = () => { document.execCommand('bold', false, null); };
  const handleItalic = () => { document.execCommand('italic', false, null); };
  const handleUnderline = () => { document.execCommand('underline', false, null); };

  const handleHighlight = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const container = range.commonAncestorContainer;
      let isHighlighted = false;
      if (container.nodeType === Node.TEXT_NODE) {
        const parent = container.parentNode;
        if (parent.style && parent.style.backgroundColor === 'yellow') isHighlighted = true;
      } else if (container.style && container.style.backgroundColor === 'yellow') {
        isHighlighted = true;
      }
      document.execCommand('backColor', false, isHighlighted ? 'transparent' : 'yellow');
    } else {
      document.execCommand('backColor', false, 'yellow');
    }
  };

  const dialogueRef = useRef(null);

  const buildQuestionsPrompt = (plainText) => {
    const lvl = (editableLevel || level || 'B1');
    return [
      `Create 6-8 discussion questions about the following dialogue. Level: ${lvl}.`,
      'Questions must be open-ended, concise (<=14 words), and encourage speaking. Return ONLY JSON:',
      '{ "questions": [ "Question 1?", "Question 2?" ] }',
      '',
      'Dialogue:',
      plainText
    ].join('\n');
  };

  const handleGenerateQuestions = async () => {
    try {
      setGenError('');
      setGenLoading(true);
      const plain = (editableDialogue || '')
        .replace(/<\/(div|p|br)[^>]*>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
      if (!plain) {
        setGenError('Dialogue is empty');
        setGenLoading(false);
        return;
      }
      const prompt = buildQuestionsPrompt(plain.slice(0, 4000));
      const raw = await fetchOpenAIText({ prompt, temperature: 0.4, maxTokens: 600 });
      let text = (raw || '').trim();
      if (text.startsWith('```')) {
        const m = text.match(/```[a-zA-Z]*\n([\s\S]*?)```/);
        if (m && m[1]) text = m[1].trim();
      }
      let parsed = null;
      try {
        parsed = JSON.parse(text);
      } catch (_) {
        const m2 = text.match(/\{[\s\S]*\}$/);
        if (m2) { try { parsed = JSON.parse(m2[0]); } catch (_) {} }
      }
      const qs = Array.isArray(parsed?.questions) ? parsed.questions.filter(q => typeof q === 'string' && q.trim()).slice(0, 12) : [];
      if (!qs.length) throw new Error('Invalid questions JSON');
      setDiscussionQuestions(qs);
      setQuestionsShared(false);
      // Save questions to own state (for restore on reopen), but don't share yet
      updateState({ questions: qs, questionsVisible: false });
    } catch (e) {
      setGenError(e?.message || 'Failed to generate');
    } finally {
      setGenLoading(false);
    }
  };

  const handleShareQuestions = () => {
    setQuestionsShared(true);
    updateState({ questions: discussionQuestions, questionsVisible: true });
  };

  const handleHideQuestions = () => {
    setQuestionsShared(false);
    updateState({ questionsVisible: false });
  };

  // Questions to display (teacher sees own, student sees from otherState)
  const studentQuestions = isStudent
    ? (questionsShared && discussionQuestions.length > 0 ? discussionQuestions : [])
    : [];

  const palette = [
    { avatar: '#667eea', bubble: '#eef2ff', border: '#c7d2fe' },
    { avatar: '#fb7185', bubble: '#fff1f2', border: '#fecdd3' },
    { avatar: '#10b981', bubble: '#ecfdf5', border: '#a7f3d0' },
    { avatar: '#3b82f6', bubble: '#eff6ff', border: '#bfdbfe' },
    { avatar: '#ec4899', bubble: '#fdf2f8', border: '#fbcfe8' },
    { avatar: '#14b8a6', bubble: '#ecfeff', border: '#a5f3fc' },
  ];

  const bubbleLines = !isEditing
    ? (editableDialogue || '')
      .replace(/<\/(div|p)>/gi, '\n')
      .replace(/<br\s*\/?>(?![^]*<)/gi, '\n')
      .replace(/<(div|p)[^>]*>/gi, '')
      .split(/\n+/)
      .map(s => s.trim())
      .filter(Boolean)
    : [];

  const nameToIndex = new Map();
  const bubbleItems = bubbleLines.map((line, idx) => {
    const plainLine = line.replace(/<[^>]*>/g, '');
    const m = plainLine.match(/^([^:]{1,50}):\s*(.*)$/);
    const name = (m && m[1]) ? m[1].trim() : 'Speaker';
    const textHtml = m ? line.replace(new RegExp('^' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ':\\s*'), '') : line;
    const initials = name.split(/\s+/).map(p => p[0]).join('').slice(0, 2).toUpperCase();
    const isRight = idx % 2 === 1;
    let assigned = nameToIndex.get(name);
    if (assigned === undefined) {
      assigned = nameToIndex.size % palette.length;
      nameToIndex.set(name, assigned);
    }
    const scheme = palette[assigned];
    return (
      <div key={idx} className={`cd-bubble-row${isRight ? ' right' : ''}`}>
        {!isRight && <div className="cd-bubble-avatar" style={{ background: scheme.avatar }}>{initials}</div>}
        <div className={`cd-bubble${isRight ? ' right' : ''}`} style={{ background: scheme.bubble, borderColor: scheme.border }}>
          <div className="cd-bubble-name">{name}</div>
          <p className="cd-bubble-text" dangerouslySetInnerHTML={{ __html: textHtml }} />
        </div>
        {isRight && <div className="cd-bubble-avatar" style={{ background: scheme.avatar }}>{initials}</div>}
      </div>
    );
  });

  return (
    <div className="cd-exercise">
      <div className="cd-header">
        <div className="cd-header-left">
          <div className="cd-header-icon">
            <svg width="20" height="20" viewBox="0 0 16 16" fill="white">
              <path d="M14 1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4.414A2 2 0 0 0 3 11.586l-2 2V2a1 1 0 0 1 1-1zM2 0a2 2 0 0 0-2 2v12.793a.5.5 0 0 0 .854.353l2.853-2.853A1 1 0 0 1 4.414 12H14a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z" />
              <path d="M5 6a1 1 0 1 1-2 0 1 1 0 0 1 2 0m4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0m4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
            </svg>
          </div>
          {isEditing ? (
            <input
              type="text"
              value={editableTitle}
              onChange={e => setEditableTitle(e.target.value)}
              className="cd-title-input"
              placeholder="Title"
            />
          ) : (
            <span className="cd-title">{editableTitle}</span>
          )}
          {isEditing ? (
            <input
              type="text"
              value={editableLevel}
              onChange={e => setEditableLevel(e.target.value)}
              className="cd-level-input"
              placeholder="Level"
            />
          ) : (
            <span className="cd-level-badge">{editableLevel}</span>
          )}
        </div>
        <div className="cd-header-actions">
          {!isEditing && !isStudent && (
            <button className="cd-icon-btn" aria-label="Edit" data-tip="Edit" onClick={() => setIsEditing(true)}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          )}
          {isEditing && (
            <>
              <button className="cd-icon-btn" aria-label="Save" data-tip="Save" onClick={handleSave}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="9,12 11,14 15,10" />
                </svg>
              </button>
              <button className="cd-icon-btn" aria-label="Cancel" data-tip="Cancel" onClick={handleCancel}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </>
          )}
          {!isEditing && onClose && (
            <button className="cd-icon-btn" aria-label="Close" data-tip="Close" onClick={onClose}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="eye-icon">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-open" />
                <path d="M2 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-closed" style={{ opacity: 0 }} />
                <circle cx="12" cy="12" r="3" className="eye-pupil" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="cd-body">
        {!isStudent && (
          <div className="cd-collapsible">
            <div
              className="cd-collapsible-header"
              onClick={() => setShowInstructions(!showInstructions)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setShowInstructions(!showInstructions)}
            >
              <span className="cd-collapsible-title">How to use this dialogue</span>
              <svg
                className={`cd-collapsible-arrow${showInstructions ? ' is-open' : ''}`}
                width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
              >
                <polyline points="9,18 15,12 9,6"></polyline>
              </svg>
            </div>
            {showInstructions && (
              <div className="cd-collapsible-content">
                <ul className="cd-instructions-list">
                  <li>Have students read through the dialogue to understand the conversation</li>
                  <li>Practice pronunciation by reading the dialogue aloud</li>
                  <li>Role-play the dialogue with different students taking different parts</li>
                  <li>Encourage students to modify the dialogue with their own ideas</li>
                  <li>Use the dialogue as a starting point for further discussion</li>
                </ul>
              </div>
            )}
          </div>
        )}

        {isEditing ? (
          <div className="cd-field">
            <label className="cd-field-label">Topic</label>
            <input
              type="text"
              value={editableTopic}
              onChange={e => setEditableTopic(e.target.value)}
              placeholder="Enter the dialogue topic..."
              className="cd-topic-input"
            />
          </div>
        ) : (
          editableTopic && <div className="cd-topic">Topic: {getShortTitle(editableTopic)}</div>
        )}

        {isEditing ? (
          <div className="cd-field">
            <label className="cd-field-label">Dialogue</label>
            <div className="cd-toolbar">
              <button type="button" onClick={handleBold} className="cd-toolbar-btn" title="Bold"><strong>B</strong></button>
              <button type="button" onClick={handleItalic} className="cd-toolbar-btn" title="Italic"><em>I</em></button>
              <button type="button" onClick={handleUnderline} className="cd-toolbar-btn" title="Underline"><u>U</u></button>
              <button type="button" onClick={handleHighlight} className="cd-toolbar-btn cd-toolbar-btn-highlight" title="Highlight">H</button>
            </div>
            <div
              ref={dialogueRef}
              contentEditable={true}
              dangerouslySetInnerHTML={{ __html: editableDialogue }}
              className="cd-dialogue-editor"
              onInput={(e) => setEditableDialogue(e.target.innerHTML)}
            />
          </div>
        ) : (
          <div className="cd-bubbles">{bubbleItems}</div>
        )}

        {/* Teacher-only: generate + share discussion questions */}
        {!isEditing && !isStudent && (
          <div className="cd-questions-card">
            <div className="cd-questions-toolbar">
              <button
                type="button"
                onClick={handleGenerateQuestions}
                disabled={genLoading}
                className="cd-questions-btn"
              >
                {genLoading ? 'Generating…' : discussionQuestions.length > 0 ? 'Regenerate questions' : 'Generate discussion questions'}
              </button>
              {discussionQuestions.length > 0 && (
                <button
                  type="button"
                  className={`cd-share-toggle${questionsShared ? ' is-shared' : ''}`}
                  onClick={questionsShared ? handleHideQuestions : handleShareQuestions}
                >
                  <span className="cd-share-dot" />
                  {questionsShared ? 'Visible to student' : 'Show to student'}
                </button>
              )}
            </div>
            {genError && <div className="cd-questions-error">{genError}</div>}
            {discussionQuestions.length > 0 && (
              <div className="cd-questions-body">
                <ol className="cd-questions-list">
                  {discussionQuestions.map((q, i) => (
                    <li key={i}>
                      <span className="cd-q-num">{i + 1}</span>
                      <span>{q}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}

        {/* Student-only: show questions when teacher has shared them */}
        {isStudent && studentQuestions.length > 0 && (
          <div className="cd-questions-card">
            <div className="cd-questions-toolbar">
              <span className="cd-collapsible-title">Discussion Questions</span>
            </div>
            <div className="cd-questions-body">
              <ol className="cd-questions-list">
                {studentQuestions.map((q, i) => (
                  <li key={i}>
                    <span className="cd-q-num">{i + 1}</span>
                    <span>{q}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </div>

      {isEditing && (
        <div className="cd-footer">
          <button className="cd-cancel-btn" onClick={handleCancel}>Cancel</button>
          <button className="cd-save-btn" onClick={handleSave}>Save</button>
        </div>
      )}
    </div>
  );
};

export default CreateDialogueExercise;
